import { Component } from '@theme/component';

/**
 * @typedef {Object} CarouselProgressRefs
 * @property {HTMLElement} track - The clickable track
 * @property {HTMLElement} thumb - The part of the track that shows how much of the carousel is in view
 */

/**
 * A slim progress bar for a scrollable carousel. It follows the scroll position of the
 * closest slideshow (or of the row named by `data-scroller`) and scrolls it when the track is clicked.
 *
 * @extends {Component<CarouselProgressRefs>}
 */
class CarouselProgress extends Component {
  /** @type {HTMLElement | null} */
  #scroller = null;

  #controller = new AbortController();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const slideshow = this.closest('slideshow-component');
    // Outside a slideshow, `data-scroller` names a native scrolling row (a CSS selector) next to the bar
    const scroller = slideshow
      ? slideshow.querySelector('slideshow-slides')
      : this.#findScroller(this.dataset.scroller);

    if (!(scroller instanceof HTMLElement)) return;

    this.#scroller = scroller;

    const { signal } = this.#controller;

    scroller.addEventListener('scroll', this.#update, { passive: true, signal });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(scroller);
    this.#update();
  }

  /**
   * Looks for the row among the neighbours of the bar: its parent, then up to two levels higher.
   * @param {string | undefined} selector
   * @returns {Element | null}
   */
  #findScroller(selector) {
    if (!selector) return null;
    let scope = this.parentElement;
    for (let level = 0; scope && level < 3; level++, scope = scope.parentElement) {
      const found = scope.querySelector(selector);
      if (found) return found;
    }
    return null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Scrolls the carousel to the point of the track that was clicked.
   * @param {MouseEvent} event
   */
  seek(event) {
    const scroller = this.#scroller;
    const { track } = this.refs;

    if (!scroller || !track) return;

    const rect = track.getBoundingClientRect();

    if (rect.width === 0) return;

    const fraction = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    scroller.scrollTo({ left: fraction * maxScroll, behavior: 'smooth' });
  }

  #update = () => {
    const scroller = this.#scroller;
    const { thumb } = this.refs;

    if (!scroller || !thumb) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    // Nothing to scroll, so nothing to show
    this.hidden = maxScroll <= 1;

    const thumbWidth = Math.min((scroller.clientWidth / scroller.scrollWidth) * 100, 100);
    const progress = maxScroll > 0 ? Math.abs(scroller.scrollLeft) / maxScroll : 0;

    thumb.style.inlineSize = `${thumbWidth}%`;
    thumb.style.insetInlineStart = `${progress * (100 - thumbWidth)}%`;
  };
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
